import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

/**
 * Dashboard de Compras (at-100, ADR 0051).
 *
 * ⚠️ **Nenhuma função deste arquivo tem `catch`.** "Zero requisições pendentes" e "não consegui
 * perguntar ao servidor" levam a conclusões opostas — a primeira faz alguém concluir que Compras
 * está em dia. Quem trata o erro é a tela, com faixa visível (regra da at-60).
 *
 * ⚠️ Os números vêm de um ESPELHO do Nasajon mantido pelo SOAC, não da API do ERP em tempo real:
 * a fonte não tem contagem, não tem ordenação e os pedidos custam dezenas de segundos por página.
 * Por isso toda resposta traz `atualizadoEm` — é o que a tela mostra como "Atualizado há X".
 */

/**
 * O grupo do centro de custo — é ele que diz à tela quais blocos montar (at-112).
 *
 * ⚠️ Os nomes vêm do eixo antigo (`PECAS`/`OUTROS`, do local de estoque) e foram mantidos de
 * propósito: é o mesmo enum no banco, e trocá-los custaria uma migration para mudar rótulo. Aqui
 * `PECAS` significa "centro de custo da oficina" — semeado por /OFICINA/i e editável na config.
 */
export type GrupoCentroCusto = 'PECAS' | 'OUTROS';

/**
 * A linha "Sem centro de custo" (at-112).
 *
 * ⚠️ Sentinela, não ausência: omitir `centrosCusto` significa "todos os centros". Sem ele, a
 * linha do card seria a única inclicável — o defeito que a at-110 corrigiu em "Sem oficina
 * informada" e a at-111 repetiu em "Local não informado".
 */
export const SEM_CENTRO_CUSTO = 'SEM_CC';

export interface CentroDeCusto {
  pk: string;
  codigo: string | null;
  descricao: string;
  grupo: GrupoCentroCusto;
  requisicoes: number;
}

export interface CentrosDeCusto {
  centros: CentroDeCusto[];
  /** `true` enquanto a camada CADASTROS não tiver rodado — o filtro não tem o que oferecer. */
  cadastroVazio: boolean;
  atualizadoEm: Record<CamadaSync, string | null>;
}

export interface FiltrosCompras {
  /**
   * Um ou mais centros de custo (pk), ou `SEM_CC`. **Vazio significa todos** — at-112.
   *
   * ⚠️ Diferente do `setor`, que abria em "Peças": com 26 centros nomeados, abrir filtrado
   * esconderia do usuário o que ele nem sabe que existe.
   */
  centrosCusto?: string[];
  de?: string;
  ate?: string;
  comprador?: string;
  /** at-109. Canônica (sem hífen) — o backend também canoniza, mas a URL fica legível. */
  placa?: string;
  /** at-109. A oficina JÁ UNIFICADA (canônica), como o card de oficinas a exibe. */
  oficina?: string;
}

export type CamadaSync =
  | 'QUENTE'
  | 'MORNA'
  | 'FRIA'
  | 'PEDIDOS'
  | 'CADASTROS'
  | 'ENTREGAS'
  | 'NEGOCIACOES';

/**
 * Por que um número não pôde ser calculado sob os filtros em vigor (at-115).
 *
 * 🔴 Vem com `total: null`, **nunca zero**. O caso real: "Aguardando atendimento" conta as
 * requisições SEM comprador, e com um comprador escolhido no topo a interseção é vazia por
 * construção. Exibir "0" ali diz "a fila está limpa", que é o oposto.
 *
 * ⚠️ A `explicacao` é do SERVIDOR e vai para a tela como veio: ela é o único conteúdo que o card
 * tem quando não há número, e recriá-la aqui faria as duas divergirem no primeiro ajuste.
 */
export interface NumeroIndisponivel {
  motivo: 'FILTRO_INCOMPATIVEL';
  /**
   * A versão de uma linha, para onde o número ficaria.
   *
   * ⚠️ A coluna do funil é estreita: a explicação inteira ali estica a altura de **todas** as
   * colunas irmãs, e as outras oito ficam com um número solto no topo de um cartão alto. O texto
   * completo vai para o `title` da coluna e para a linha de apoio do indicador do topo.
   */
  curto: string;
  explicacao: string;
}

export interface LinhaDoFunil {
  estado: string;
  rotulo: string;
  /** `null` quando `indisponivel` está preenchido. */
  total: number | null;
  /** Resíduo antigo dos estados-cemitério. Nunca somar ao total. */
  foraDaJanela?: number;
  /**
   * Linha de apoio sob o número, quando o rótulo sozinho não diz o que a coluna reúne.
   *
   * ⚠️ at-117: substituiu `sobreposicao?: boolean`, que existia só para "Em aprovação" ("já
   * contado nas pendentes"). Com a regra do workflow o funil voltou a ser disjunto — não há mais
   * coluna contida em outra, e a soma fecha com a base.
   */
  legenda?: string;
  indisponivel?: NumeroIndisponivel;
}

export interface ResumoCompras {
  centrosCusto: string[];
  /**
   * `true` quando o cadastro de locais de uso ainda não foi sincronizado e o filtro de centro de
   * custo não pôde ser aplicado — o painel está mostrando a empresa inteira. Precisa ser dito:
   * quem escolheu um centro e vê os números de todo mundo tiraria a conclusão errada.
   */
  centroCustoNaoAplicado: boolean;
  janela: { de: string; ate: string };
  atualizadoEm: Record<CamadaSync, string | null>;
  observadoDesde: string | null;
  kpis: {
    /** Requisições cadastradas no período, em qualquer situação. Opcional: backend anterior não envia. */
    totalRequisicoes?: number;
    /** at-123: linhas de item dessas mesmas requisições. Opcional: backend anterior não envia. */
    totalItens?: number;
    /** Requisições do Caminho VIP no período. Opcional: backend anterior não envia. */
    caminhoVip?: number;
    /** Linhas de item dessas mesmas requisições do Caminho VIP. Opcional: backend anterior não envia. */
    caminhoVipItens?: number;
    /** `null` com `aguardandoAtendimentoIndisponivel` preenchido — ver `NumeroIndisponivel`. */
    aguardandoAtendimento: number | null;
    aguardandoAtendimentoIndisponivel?: NumeroIndisponivel;
    emAtendimento: number;
    emCotacao: number;
    atendidasNaJanela: number;
    /** Rótulo honesto do KPI acima — é aproximação até haver transições suficientes. */
    definicaoAtendidas: string;
    /**
     * "Tempo na fila de Compras". ⚠️ Desde a at-120 mede as requisições EM ABERTO (do cadastro até
     * hoje) e as que já VIRARAM PEDIDO (do cadastro até a emissão do primeiro pedido), juntas — o
     * nome do campo é contrato desde a at-117 e não mudou.
     */
    idadePendentesDias: ResumoDeDiasNaFila;
    /** Desde quando o elo pedido → requisição existe no espelho: antes disso, nada é contado. */
    atePedidoObservadoDesde: string | null;
  };
  funil: LinhaDoFunil[];
  tempoObservado: {
    disponivel: boolean;
    amostra: number;
    minimoNecessario?: number;
    medianaDiasPendenteAteCompra?: number | null;
    medianaDiasPedidoAteAtendida?: number | null;
  };
}

export interface EvolucaoMensalCompras {
  meses: Array<{ mes: string; pedidos: number; valor: number; parcial: boolean }>;
  cancelados: number;
  /** Totais do período inteiro — a série diz como variou, estes dizem quanto foi. */
  totais: { pedidos: number; valor: number };
  atualizadoEm: Partial<Record<CamadaSync, string | null>>;
  /**
   * O que ainda não se aplica — só o centro de custo, e só quando o cadastro não foi sincronizado.
   * ⚠️ O comprador SAIU desta lista na at-115: ele passou a valer de verdade aqui.
   */
  filtrosNaoAplicados: string[];
  /** Ver `FornecedoresCompras.semOrigemConhecida`. */
  semOrigemConhecida?: number;
  /**
   * A série cobre o período escolhido (at-115), em vez dos 12 meses fixos.
   *
   * ⚠️ Só acontece quando o usuário escolheu período: sem escolha, `resolverJanela` cairia no
   * padrão de 30 dias e o gráfico de 12 meses viraria dois para quem não filtrou nada.
   */
  periodoAplicado: boolean;
  /** O período escolhido passou do teto de colunas e a série foi cortada — a tela diz isso. */
  serieCortada: boolean;
}

export interface EntregaNoPrazo {
  /**
   * 🔴 Campo de CONTRATO, e a tela é obrigada a exibi-lo junto do número.
   *
   * A peça que vai para oficina externa é avisada em grupo de WhatsApp e nunca entra no Nasajon —
   * não existe evento de chegada. Sem a ressalva, "92% no prazo" é lido como a pontualidade dos
   * fornecedores, quando é a pontualidade **do que passou pelo estoque interno**.
   */
  soOficinaInterna: boolean;
  /**
   * De onde o número vem. Desde a at-128, o elo pedido → nota da API do Nasajon (era o relatório
   * subido à mão, `RELATORIO_IMPORTADO`).
   */
  fonte: 'API_NASAJON';
  /**
   * O que este bloco IGNORA do filtro do painel. Desde a at-125 comprador e centro de custo
   * valem (pelo elo pedido → negociação → requisição); só o centro de custo pode ficar de fora,
   * quando o cadastro de locais de uso não está sincronizado.
   */
  filtrosNaoAplicados: string[];
  disponivel: boolean;
  percentualNoPrazo: number | null;
  noPrazo: number;
  atrasadas: number;
  medianaDiasDeAtraso: number | null;
  cobertura: {
    /** Pedidos cuja última entrada cai no período. */
    pedidosNoPeriodo: number;
    medidas: number;
    semPrevisao: number;
    /** Previsão anterior à emissão do pedido — dado impossível, descartado. */
    previsaoImpossivel: number;
    /** Nota entrada antes de o pedido existir — dado impossível, descartado. */
    chegadaAntesDaEmissao: number;
    /** Contados, não descartados: podem ser retirada no balcão ou campo preenchido por padrão. */
    previsaoNoDiaDaEmissao: number;
    /** Só com filtro de comprador/centro: pedidos sem origem conhecida, deixados de fora. */
    semOrigemConhecida?: number;
  };
  /**
   * 🔴 O frescor da chegada (at-128): o último SUCESSO da sincronização com o Nasajon. Sem esta
   * data ao lado, um dado parado passa por atual. `ultimoSucessoEm: null` = nunca sincronizou.
   */
  sincronizacao: FrescorDasEntregas;
  fornecedores: Array<{
    fornecedor: string;
    entregas: number;
    percentualNoPrazo: number;
    atrasoMedioDias: number;
  }>;
  janela: { de: string; ate: string };
  atualizadoEm: string | null;
}

/*
 * at-125: `buscar`, e não `axios.get` com `params` — o `params` do axios serializa lista como
 * `campo[]=a&campo[]=b`, e o backend espera CSV. Enquanto o bloco ignorava os filtros isso não
 * aparecia; com comprador e centro de custo valendo, o filtro chegaria quebrado.
 */
export const fetchEntregaNoPrazo = (filtros: FiltrosCompras) =>
  buscar<EntregaNoPrazo>('entrega-no-prazo', filtros);

export interface PedidosDoMes {
  mes: string;
  /** Do mês inteiro, não da página — é o número que a coluna do gráfico exibia. */
  total: number;
  pagina: number;
  porPagina: number;
  pedidos: Array<{
    /** at-110: a chave do clique no número do pedido. */
    id: string;
    numero: string | null;
    emissao: string | null;
    fornecedorNome: string | null;
    situacao: string | null;
    total: number;
    previsaoDeEntrega: string | null;
    operacao: string | null;
    /** at-90: comentários do pedido — o selo na linha. */
    comentarios?: number;
  }>;
  atualizadoEm: string | null;
}

/**
 * Os pedidos que compõem uma coluna do gráfico de evolução.
 *
 * 🔴 **Passou a levar os filtros na at-115**, porque a coluna passou a aplicá-los (comprador e
 * centro de custo, pelo elo pedido → negociação → requisição). Até aqui ela não os mandava — e
 * estava certo, porque o gráfico também não os aplicava. Agora, não mandar faria a lista somar
 * mais do que a coluna que a abriu, que é a divergência que a at-110 existiu para fechar.
 *
 * ⚠️ `de`/`ate` viajam junto e o backend os **ignora**: o recorte de período deste clique é o
 * próprio mês. Ver `ComprasPedidosQueryDto`.
 */
export async function fetchPedidosDoMes(
  mes: string,
  pagina: number,
  porPagina = 25,
  filtros: FiltrosCompras = {},
  /** at-90: os filtros da lista, em `AND` sobre a coluna clicada. */
  daLista: FiltrosDaLista = {},
): Promise<PedidosDoMes> {
  // ⚠️ Pelo `buscar` compartilhado, e não por `params` do axios: é ele que serializa lista como
  // CSV (`centrosCusto=a,b`), que é o formato que o DTO espera. O `params` do axios produziria
  // `centrosCusto[]=a`, e o filtro chegaria vazio.
  return buscar<PedidosDoMes>('pedidos', { ...filtros, ...daLista, mes, pagina, limite: porPagina });
}

/**
 * Tempo na fila por comprador — at-119.
 *
 * 🔴 É a MESMA métrica do KPI "Tempo na fila de Compras", agrupada por pessoa — e sai da mesma
 * consulta no backend. Desde a at-120 ela junta as requisições em aberto (cadastro até hoje) e as
 * que já viraram pedido (cadastro até a emissão do primeiro pedido). Não é o tempo de atendimento
 * por comprador da at-116 (`GET /compras/dashboard/tempo-por-comprador`), que mede transições
 * observadas pelo SOAC, espera a Nasajon e não tem consumidor na tela desde então.
 */
export interface FilaPorComprador {
  /** O resumo da fila inteira — o mesmo número do KPI do topo, que a tela confronta. */
  total: ResumoDeDiasNaFila;
  compradores: Array<
    {
      id: string;
      /** Nulo com `id` preenchido é o comprador fora do diretório do ERP — nunca exibir o uuid. */
      nome: string | null;
    } & ResumoDeDiasNaFila
  >;
  /** Sem analista na requisição: declaradas, nunca somadas a alguém. */
  semComprador: number;
  /** Desde quando o elo pedido → requisição existe no espelho (at-120). */
  atePedidoObservadoDesde: string | null;
  janela: { de: string; ate: string };
}

export interface ResumoDeDiasNaFila {
  amostra: number;
  mediana: number | null;
  p90: number | null;
  max: number | null;
  /** at-120: quantas das medidas ainda estão na fila e quantas já viraram pedido. */
  emAberto: number;
  finalizadas: number;
}

export const fetchFilaPorComprador = (f: FiltrosCompras) =>
  buscar<FilaPorComprador>('fila-por-comprador', f);

export interface CompradoresCompras {
  compradores: Array<{
    analistaCompra: string;
    nome: string | null;
    requisicoes: number;
    itensDistintos: number;
    pendentes: number;
  }>;
  /**
   * O comprador escolhido no filtro do topo, quando há um (at-115).
   *
   * 🔴 É o que faz o card trocar de FORMA: com um comprador filtrado o gráfico teria uma barra só,
   * e barra única não compara nada — ele vira o resumo da pessoa (decisão do Victor, 24/09/2026).
   * O nome vem daqui, e não da primeira linha da lista, porque a lista pode voltar vazia — que é
   * justamente quando a explicação importa.
   */
  recortadoPor: { id: string; nome: string | null } | null;
  semNome: number;
  totais: { itensDistintos: number; requisicoes: number };
  janela: { de: string; ate: string };
}

export interface FornecedoresCompras {
  fornecedores: Array<{ pk: string | null; nome: string | null; pedidos: number; valor: number }>;
  janela: { de: string; ate: string };
  filtrosNaoAplicados: string[];
  /**
   * Pedidos do período que nenhum recorte por origem alcança — compra direta, sem requisição
   * (~4 em 100, medido na at-109). Só vem quando há filtro de comprador, placa, oficina ou centro
   * de custo. Sem este número, o total daqui não fecha com o da aba Pedidos e parece erro.
   */
  semOrigemConhecida?: number;
}

/**
 * O recorte do card "Requisições por oficina" (at-117). Espelha `IDENTIFICACAO_DE_OFICINA` do
 * backend, que é quem valida.
 *
 * ⚠️ Um valor só, não uma lista: as três opções são mutuamente exclusivas ("todas" já é a união
 * das outras duas). É por isso que o controle é um `select`, e não o modal de caixas de seleção
 * do filtro de status (at-112).
 */
export type IdentificacaoDeOficina = 'todas' | 'identificadas' | 'nao_identificadas';

export interface OficinasCompras {
  oficinas: Array<{
    oficina: string;
    requisicoes: number;
    /** Ainda sendo compradas (pendente, em cotação, em negociação). */
    abertas: number;
    /** Já viraram pedido — é esta que interessa a quem liga para confirmar o recebimento. */
    aguardandoEntrega: number;
    /**
     * Quantas daquela linha entraram pela **salvaguarda** (at-113): a classificação financeira
     * delas não é de compra de peça, e elas só estão no card porque alguém escreveu o nome da
     * oficina. É por linha, e não só no total, porque é isso que diz ONDE está a mistura.
     */
    foraDaLista: number;
    locais: string[];
  }>;
  /**
   * Obrigatória na tela — e desde a at-113 o denominador é o UNIVERSO (as compras de peça), não a
   * base inteira. Antes dela a cobertura dava 35%, porque vidro e reforma predial entravam na
   * conta; hoje dá ~90%, e o que sobra é falta de preenchimento na origem.
   */
  cobertura: { comOficina: number; total: number };
  /**
   * O recorte aplicado no card (at-117). Eco do servidor: sem ele a tela não tem como saber que o
   * filtro foi honrado — o frontend anterior a esta entrega nem o envia.
   */
  identificacao?: IdentificacaoDeOficina;
  /**
   * Quantas linhas a tabela está mostrando, de quantas o período tem. Só vem com filtro aplicado.
   *
   * ⚠️ Existe porque `cobertura` continua descrevendo o PERÍODO INTEIRO: recalculada sobre o
   * recorte, ela diria "0 de 14 (0%)" ao lado da lista das não identificadas — verdade inútil, e
   * que some com o denominador que dá sentido ao filtro.
   */
  recorte?: { mostradas: number; de: number };
  /** O total de `foraDaLista`, para o rodapé do card. */
  foraDaListaTotal: number;
  /**
   * 🔴 O cadastro de classificações ainda não sincronizou, então o universo NÃO foi aplicado e o
   * card está mostrando a base inteira. A tela precisa declarar isso: sem a faixa, o número volta
   * a ser o de antes da at-113 e ninguém tem como saber por quê.
   */
  universoNaoAplicado?: true;
  janela: { de: string; ate: string };
}

export interface UltimasRequisicoes {
  requisicoes: Array<{
    /** at-129: comentários da requisição (raiz + respostas) — o selo na linha. */
    comentarios?: number;
    /** at-110: a chave do clique no número — é por ela que o detalhe da requisição é buscado. */
    id: string;
    codigo: string;
    dataCadastro: string | null;
    situacao: string | null;
    estadoWorkflow: string | null;
    compradorNome: string | null;
    /** Quem pediu. Nulo enquanto o de-para de nomes não for preenchido. */
    solicitanteNome: string | null;
    solicitanteId: string | null;
    oficina: string | null;
    placa: string | null;
    justificativaResumo: string | null;
    local: string | null;
    /** at-109: quantos itens a requisição tem — pedido pela área de Compras. */
    itens: number;
    /**
     * at-119: há quantos dias está na fila de Compras — a MESMA conta do KPI, feita no servidor.
     * 🔴 Nulo fora da fila (pedido gerado, atendida, não aprovada): ali "dias desde o cadastro" é
     * a idade do registro, não espera — foi por isso que a coluna "Dias" saiu na at-112.
     */
    diasNaFila: number | null;
    /**
     * at-120: só na lista do "Tempo na fila de Compras". `EM_ABERTO` conta até hoje,
     * `VIROU_PEDIDO` até a emissão do primeiro pedido. Nulo nas outras listas.
     */
    filaSituacao?: 'EM_ABERTO' | 'VIROU_PEDIDO' | null;
    /** at-113: a classificação financeira da requisição, quando o local de uso a tem. */
    classificacaoFinanceira: string | null;
    /**
     * Entrou pela salvaguarda do card de oficinas: a classificação dela não é de compra de peça,
     * e ela está aqui porque tem oficina preenchida. Só vem preenchido quando a lista foi aberta
     * por um clique numa oficina.
     */
    foraDaLista: boolean;
  }>;
  /** Do RECORTE inteiro, não da página — é o número que a tela confronta com o card clicado. */
  total: number;
  pagina: number;
  porPagina: number;
  janela: { de: string; ate: string };
  /**
   * O recorte é vazio por construção sob os filtros em vigor (at-115) — hoje só "Aguardando
   * atendimento" com um comprador escolhido. A lista mostra a explicação, nunca "Nenhuma
   * requisição neste recorte", que é a frase de um recorte legitimamente vazio.
   */
  indisponivel?: NumeroIndisponivel;
}

/**
 * Os recortes que um clique num card abre. Espelha o enum do backend
 * (`RECORTES_DE_REQUISICAO`), que é quem decide o `where`.
 *
 * ⚠️ `em_cotacao_total` é o card do TOPO (união de cotação + negociação) e `em_cotacao` é o do
 * FUNIL (só cotação). Os dois existem porque exibem números diferentes com o mesmo rótulo.
 */
export type RecorteDeRequisicao =
  | 'pendente_sem_comprador'
  | 'pendente_com_comprador'
  /**
   * at-117: as pendentes que ainda não passaram pelo aprovador — em aprovação, com workflow não
   * iniciado ou reprovadas.
   */
  | 'antes_de_compras'
  | 'em_cotacao'
  | 'em_cotacao_total'
  | 'em_negociacao'
  | 'pedido_gerado'
  | 'pedido_gerado_fora_da_janela'
  | 'parcialmente_atendida'
  | 'parcialmente_atendida_fora_da_janela'
  | 'atendida'
  | 'cancelada'
  | 'pendentes'
  /** Todas as requisições cadastradas no período (card "Total de requisições"). */
  | 'total'
  /** Requisições do Caminho VIP (texto "CAMINHO VIP" na justificativa). */
  | 'caminho_vip';

/**
 * As requisições que compõem um card do painel.
 *
 * 🔴 Sem catch, como o resto deste arquivo: lista vazia por erro seria lida como "este card não
 * tem requisição nenhuma" — a conclusão oposta à verdadeira, num painel de operação.
 */
export async function fetchRequisicoesDoRecorte(opcoes: {
  /**
   * ⚠️ `situacoes` entra aqui quando o clique veio de um card com filtro próprio (at-110, multi
   * na at-112); `centrosCusto`, quando veio do card de centros; `solicitante`, do card de
   * solicitantes (at-111).
   */
  filtros: FiltrosCompras & {
    situacoes?: string[];
    centrosCusto?: string[];
    solicitante?: string;
  };
  /** Ausente = "as últimas do período", que é o bloco do rodapé do painel. */
  recorte?: RecorteDeRequisicao;
  pagina: number;
  porPagina?: number;
  /** at-90: os filtros da lista, em `AND` sobre o recorte. */
  daLista?: FiltrosDaLista;
}): Promise<UltimasRequisicoes> {
  const { filtros, recorte, pagina, porPagina = 25, daLista } = opcoes;
  // 🔴 `buscar`, e não `params` do axios (at-112): o axios serializa array como `campo[]=a`, e o
  // backend espera CSV — o `forbidNonWhitelisted` devolveria 400 na tela inteira.
  return buscar<UltimasRequisicoes>('requisicoes', {
    ...filtros,
    ...daLista,
    ...(recorte ? { recorte } : {}),
    pagina,
    limite: porPagina,
  });
}

/**
 * Uma sincronização acontecendo AGORA. Nulo quando não há nenhuma em curso.
 *
 * 🔴 `ultimoSinal` é o que separa "trabalhando" de "travada": uma varredura longa e saudável tem
 * `desde` antigo e `ultimoSinal` recente. Sem ele, um job morto pareceria um job lento para sempre.
 */
export interface SyncEmAndamento {
  desde: string;
  etapa: string | null;
  etapaAtual: number | null;
  etapasTotal: number | null;
  paginas: number;
  ultimoSinal: string | null;
}

export interface EstadoDaCamada {
  ultimoSucesso: string | null;
  linhas: number | null;
  paginasDoUltimoSucesso: number | null;
  /** Quanto a última sincronização bem-sucedida levou. Nulo = não há execução concluída medida. */
  duracaoSegundos: number | null;
  parcial: boolean;
  avisoDoUltimoSucesso: string | null;
  ultimaTentativa: string | null;
  statusDaUltimaTentativa: string | null;
  erro: string | null;
  /**
   * A última tentativa falhou por ABANDONO (o processo morreu no meio), e não por recusa do ERP.
   *
   * É rotina — acontece a cada deploy que pega uma varredura longa — e o saneamento a resolve no
   * ciclo seguinte. A tela usa isto para escolher o peso da nota: rotina sai discreta, falha real
   * sai como aviso. Quem classifica é o backend, comparando com a constante que gravou a linha.
   */
  ultimaTentativaAbandonada: boolean;
  /**
   * O ritmo prometido por esta camada, em segundos. Vem do backend porque lá vivem os crons —
   * repetir a cadência aqui faria a régua de frescor mentir no dia em que um cron mudasse.
   */
  cadenciaSegundos: number;
  /**
   * at-125: camada sem agendamento, só disparo manual (hoje, ENTREGAS). A régua de frescor não se
   * aplica a ela — envelheceria para sempre e apareceria "Atrasado" sem nada errado.
   */
  soManual?: boolean;
  /**
   * Esta camada está parada AGORA por estar fora do horário de expediente (at-103).
   *
   * 🔴 Quem decide isto é o servidor, e a tela não pode recalcular: seria uma segunda definição do
   * mesmo corte, no fuso errado — o navegador usa o fuso da máquina de quem olha, e o cron usa o de
   * Brasília. É a mesma armadilha que fez o gráfico e a lista de compras discordarem (at-100).
   *
   * ⚠️ Sem este campo a régua de frescor mentiria todo fim de semana: numa segunda de manhã a
   * camada Quente apareceria "Atrasada desde sexta às 19h", em vermelho, com o sync funcionando
   * exatamente como projetado.
   */
  paradaPorHorario: boolean;
  nuncaExecutada: boolean;
  emAndamento: SyncEmAndamento | null;
}

export type ModoSync = 'NORMAL' | 'DIARIO';

/**
 * Pausa e ritmo das leituras — at-108.
 *
 * São **dois controles independentes**, e não três posições de um seletor (decisão do Victor,
 * 18/09/2026): pausar é emergência sobre um ritmo, e retomar devolve o ritmo que estava gravado,
 * sem quem retoma ter de lembrar qual era. Pausado vence o ritmo enquanto durar.
 *
 * 🔴 A autoria é o que dá sentido ao aviso na tela: "pausado" sem quem e quando manda alguém
 * procurar um responsável que o sistema conhece.
 */
export interface EstadoDaSincronizacao {
  pausado: boolean;
  pausadoPor: { id: string; nome: string } | null;
  pausadoEm: string | null;
  modo: ModoSync;
  modoPor: { id: string; nome: string } | null;
  modoEm: string | null;
  /**
   * A hora da leitura no ritmo diário. Vem do servidor de propósito — a tela nunca escreve "19h"
   * por conta própria, senão o texto mentiria no dia em que o horário mudasse.
   */
  horaDaLeituraDiaria: number;
  /** Último sucesso da camada que marca a leitura do dia. Nulo = ainda não houve. */
  ultimaLeituraDiaria?: string | null;
}

export interface StatusCompras {
  configurado: boolean;
  camadas: Record<CamadaSync, EstadoDaCamada>;
  /**
   * ⚠️ **Opcional de propósito**: um backend anterior à at-108 não manda este bloco, e a tela trata
   * a ausência como "normal, não pausado" — é a janela da amarra backend-antes-do-frontend.
   */
  sincronizacao?: EstadoDaSincronizacao;
  observadoDesde: string | null;
  /**
   * A janela de expediente por extenso ("de segunda a sexta, das 8h às 19h"), servida pelo
   * servidor para a tela não reescrever o horário por conta própria — no dia em que ele mudar, o
   * texto muda junto com o cron ou não muda.
   */
  janelaDeExpediente: string;
}

/** Uma execução passada (ou em curso) do sync. Alimenta a aba Sincronização. */
export interface ExecucaoDeSync {
  id: string;
  camada: CamadaSync;
  status: 'EM_ANDAMENTO' | 'CONCLUIDO' | 'FALHOU';
  iniciadoEm: string;
  concluidoEm: string | null;
  duracaoSegundos: number | null;
  linhas: number;
  diff: number;
  paginas: number;
  parcial: boolean;
  /** Em CONCLUIDO é o aviso (varredura parcial); em FALHOU é o motivo. O status diz qual. */
  erro: string | null;
  etapa: string | null;
  etapaAtual: number | null;
  etapasTotal: number | null;
  ultimoSinal: string | null;
}

export interface HistoricoDeSync {
  execucoes: ExecucaoDeSync[];
}

/**
 * Os parâmetros que as rotas do painel aceitam.
 *
 * ⚠️ Chave nova aqui **precisa existir no DTO do backend**: o `ValidationPipe` tem
 * `forbidNonWhitelisted`, então um parâmetro que a tela envia e o DTO não declara vira 400 na tela
 * inteira — e nem build, nem tipos, nem teste de service atravessam o pipe para acusar.
 */
/**
 * Os filtros que o usuário aplica DENTRO de uma lista aberta por um card (transversal/at-90).
 *
 * 🔴 Prefixo `lista*` de propósito: os nomes do painel (`comprador`, `placa`, `situacoes`) DEFINEM
 * o recorte que o card contou, e estes só o estreitam. Com o mesmo nome, o filtro da lista
 * sobrescreveria o do card no objeto montado aqui, e a lista deixaria de ser subconjunto do número
 * clicado. Texto livre ("contém") para pessoas, fornecedor, oficina e placa.
 */
export interface FiltrosDaLista {
  listaBusca?: string;
  listaSituacoes?: string[];
  listaPlaca?: string;
  listaComprador?: string;
  listaSolicitante?: string;
  listaFornecedor?: string;
  listaOficina?: string;
  listaDe?: string;
  listaAte?: string;
}

type ParametrosDeBusca = FiltrosCompras & FiltrosDaLista & {
  limite?: number;
  situacao?: string;
  /** at-112: multi-seleção. O singular acima fica para o filtro que ainda é de um valor só. */
  situacoes?: string[];
  centrosCusto?: string[];
  pagina?: number;
  busca?: string;
  por?: AgrupamentoDePedido;
  /** at-110: a pk (pedidos) ou o nome da nota (entregas) — ver cada função. */
  fornecedor?: string;
  /** at-111: o identificador de quem pediu, no ERP. */
  solicitante?: string;
  /** at-115: o mês da coluna do gráfico de evolução (AAAA-MM). */
  mes?: string;
  /** at-121: a linha "Sem requisição de origem" dos cards da aba Pedidos. */
  origem?: 'com' | 'sem';
};

function params(filtros: ParametrosDeBusca) {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    /*
     * ⚠️ Lista vira CSV, e lista VAZIA não vira parâmetro (at-112).
     *
     * `String(['a'])` já daria `'a'` e `String([])` daria `''`, então o caminho antigo
     * "funcionaria" por acidente — até alguém passar `situacoes: []` esperando "nenhuma" e
     * receber "todas". Explícito aqui, uma vez, em vez de em cada chamador.
     */
    if (Array.isArray(valor)) {
      if (valor.length) busca.set(chave, valor.join(','));
      continue;
    }
    if (valor !== undefined && valor !== null && valor !== '') busca.set(chave, String(valor));
  }
  return busca.toString();
}

async function buscar<T>(caminho: string, filtros: ParametrosDeBusca = {}): Promise<T> {
  const qs = params(filtros);
  const { data } = await axios.get<T>(`${BACKEND_URL}/compras/dashboard/${caminho}${qs ? `?${qs}` : ''}`);
  return data;
}

export const fetchResumoCompras = (f: FiltrosCompras) => buscar<ResumoCompras>('resumo', f);
export const fetchEvolucaoMensalCompras = (f: FiltrosCompras) => buscar<EvolucaoMensalCompras>('evolucao-mensal', f);
/**
 * at-110: `situacao` é o filtro do PRÓPRIO card, não do painel.
 *
 * ⚠️ Por isso ele não entra em `FiltrosCompras`: quem está ali refaz a tela inteira, e este só
 * refaz este bloco. Misturar os dois faria escolher "Em cotação" na carga por comprador mudar o
 * funil, a evolução e os fornecedores — números que ninguém pediu para filtrar.
 */
export const fetchCompradoresCompras = (f: FiltrosCompras & { situacoes?: string[] }) =>
  buscar<CompradoresCompras>('compradores', f);
export const fetchFornecedoresCompras = (f: FiltrosCompras) => buscar<FornecedoresCompras>('fornecedores', f);
export const fetchOficinasCompras = (f: FiltrosCompras & { identificacao?: IdentificacaoDeOficina }) =>
  buscar<OficinasCompras>('oficinas', f);
export const fetchUltimasRequisicoes = (f: FiltrosCompras & { limite?: number; situacoes?: string[] }) =>
  buscar<UltimasRequisicoes>('requisicoes', f);
export const fetchStatusCompras = () => buscar<StatusCompras>('status');
export const fetchHistoricoDeSync = () => buscar<HistoricoDeSync>('sync-historico');

// ---------------------------------------------------------------------------------------------
// at-109 — os indicadores pedidos pela área de Compras.
//
// 🔴 O pedido de compra do Nasajon NÃO tem comprador, placa nem oficina. Os três vêm da
// requisição que o originou, pelo caminho pedido → negociação → requisição, resolvido no
// servidor. Por isso `compradorNome`, `placa` e `oficina` podem ser nulos: é compra direta, sem
// requisição — e a tela conta quantas são em vez de escondê-las.
// ---------------------------------------------------------------------------------------------

/** O que se pode afirmar sobre a entrega de um pedido que ainda não chegou. */
export type SituacaoDaPrevisao = 'AGUARDANDO' | 'PREVISAO_VENCIDA' | 'SEM_PREVISAO';

/**
 * A chegada contra a previsão (at-128). Sem chegada, vale `SituacaoDaPrevisao` — e "previsão
 * vencida" continua sem afirmar atraso: a peça de oficina externa nunca entra no ERP.
 */
export type SituacaoDaChegada =
  | 'CHEGOU_NO_PRAZO'
  | 'CHEGOU_ATRASADO'
  | 'CHEGOU_SEM_PREVISAO'
  | SituacaoDaPrevisao;

/** A situação que Compras usa (at-128), mais o pedido ainda não sincronizado. */
export type SituacaoGerencialDoPedido =
  | 'aberto'
  | 'aguardando_documento'
  | 'parcialmente_liquidado'
  | 'liquidado'
  | 'cancelado'
  | 'nao_sincronizada';

/** O frescor da chegada: último sucesso da sincronização e até que dia vão as entradas. */
export interface FrescorDasEntregas {
  ultimoSucessoEm: string | null;
  entradasAte: string | null;
  pedidos: number;
}

export interface RequisicoesPorPlaca {
  placas: Array<{
    placa: string;
    requisicoes: number;
    /** Ainda sendo comprada. */
    abertas: number;
    /** Já virou pedido — é esta que interessa a quem cobra a oficina. */
    aguardandoEntrega: number;
    itens: number;
    compradores: string[];
    ultimaEm: string | null;
  }>;
  total: number;
  pagina: number;
  porPagina: number;
  /**
   * 🔴 Obrigatória na tela: a placa sai do TEXTO da justificativa e aparece em ~72% das
   * requisições. Um ranking sobre três quartos da base, sem dizer isso, passa por retrato do todo.
   */
  cobertura: { comPlaca: number; total: number };
  janela: { de: string; ate: string };
}

export interface PedidosComOrigem {
  pedidos: Array<{
    /** at-110: a chave do clique no número do pedido. */
    id: string;
    numero: string | null;
    emissao: string | null;
    fornecedorNome: string | null;
    situacao: string | null;
    total: number;
    itens: number;
    previsaoDeEntrega: string | null;
    situacaoPrevisao: SituacaoDaPrevisao;
    /** at-128: a situação de Compras (o `situacao_gerencial` do Nasajon). */
    situacaoGerencial: SituacaoGerencialDoPedido;
    /** Data da última nota de entrada; `null` = sem nota até a última sincronização. */
    entregueEm: string | null;
    notasDeEntrada: number;
    /** at-128: a chegada contra a previsão — é o que a coluna "Chegada" exibe. */
    situacaoChegada: SituacaoDaChegada;
    /** at-129: comentários do PRÓPRIO pedido (raiz + respostas) — o selo na linha. */
    comentarios?: number;
    requisicaoCodigo: string | null;
    compradorNome: string | null;
    placa: string | null;
    oficina: string | null;
  }>;
  total: number;
  pagina: number;
  porPagina: number;
  /** O frescor da chegada (at-128): sem ele, "sem nota de entrada" passa por afirmação atual. */
  sincronizacao: FrescorDasEntregas;
  /** Compra direta, sem requisição de origem — ~4 de cada 100 pedidos. */
  semOrigemNaPagina: number;
  cobertura: { comOrigemNaPagina: number; naPagina: number };
  /**
   * 🔴 `true` enquanto a camada de negociações não tiver rodado. Nesse estado a cobertura é 0 de N
   * e NÃO pode ser lida como "nenhum pedido tem origem" — foi a leitura do primeiro teste manual.
   */
  origemNaoSincronizada: boolean;
  /** Presente enquanto os itens dos pedidos não estiverem no espelho (o backfill não rodou). */
  centroCustoNaoAplicado?: string[];
  janela: { de: string; ate: string };
}

/** at-121: `centro_custo` e `solicitante` — os cinco cards da aba Pedidos. */
export type AgrupamentoDePedido = 'placa' | 'oficina' | 'comprador' | 'centro_custo' | 'solicitante';

/** A soma de um grupo de pedidos — vale para as linhas e para as duas linhas de resíduo. */
export interface SomaDePedidos {
  pedidos: number;
  itens: number;
  valor: number;
  /**
   * transversal/at-90: quantos pedidos do grupo têm ao menos um comentário — o selo da linha.
   * Opcional: o backend anterior a esta entrega não manda, e ausente vale "sem selo".
   */
  comComentarios?: number;
}

export interface PedidosAgrupados {
  por: AgrupamentoDePedido;
  grupos: Array<
    SomaDePedidos & {
      /** O rótulo exibido. */
      grupo: string;
      /** at-121: o que o clique envia à lista (placa, pk, uuid, nome canônico da oficina). */
      chave: string;
      codigo: string | null;
      /** Só no card de oficina: pedidos que entraram pela salvaguarda "oficina preenchida". */
      foraDaLista: number;
    }
  >;
  /** at-121: quantos grupos o recorte tem — a paginação é sobre este número. */
  total: number;
  pagina: number;
  porPagina: number;
  /**
   * 🔴 at-121: os dois resíduos. Linhas + `semOrigem` + `semAtributo` = `cobertura.total` — é com
   * eles que o card prova que ninguém fica de fora.
   */
  semOrigem: SomaDePedidos & { rotulo: string };
  /** `chave` nula = a linha não abre lista (não há sentinela para aquele atributo). */
  semAtributo: SomaDePedidos & { rotulo: string; chave: string | null };
  cobertura: { comAtributo: number; total: number };
  /** Só no card de oficina: pedidos que não são compra de peça (vidro, reforma...). */
  foraDoUniverso?: number;
  universoNaoAplicado?: boolean;
  origemNaoSincronizada: boolean;
  centroCustoNaoAplicado?: string[];
  janela: { de: string; ate: string };
}

/**
 * Os KPIs e o funil da aba Pedidos (at-121).
 *
 * 🔴 Contados com o MESMO `where` da lista: o clique em qualquer número abre o que ele contou.
 * Desde a at-128 a situação é a GERENCIAL do Nasajon — a que Compras usa.
 */
export interface ResumoPedidos {
  kpis: {
    totalPedidos: number;
    valorTotal: number;
    abertos: number;
    aguardandoDocumento: number;
    parcialmenteLiquidados: number;
    liquidados: number;
    /** Fora de `totalPedidos`. */
    cancelados: number;
    /** Pedido que a sincronização ainda não reclassificou — declarado, nunca somado a um vizinho. */
    naoSincronizados: number;
    abertosComPrevisaoVencida: number;
    itens: { total: number; cobertura: { comItens: number; total: number } };
    /** `null` quando não há pedido aberto — nunca "0 dias". */
    diasEmAberto: { amostra: number; mediana: number | null; p90: number | null; max: number | null } | null;
  };
  funil: Array<{
    estado: string;
    rotulo: string;
    total: number;
    /** As situações que o clique na coluna envia à lista. */
    situacoes: string[];
    legenda?: string;
  }>;
  cobertura: { comOrigem: number; total: number };
  origemNaoSincronizada: boolean;
  centroCustoNaoAplicado?: string[];
  janela: { de: string; ate: string };
}

/**
 * Os filtros que uma linha de card ou um KPI da aba Pedidos acrescentam à lista (at-121).
 *
 * 🔴 São os MESMOS que produziram o número — montados por quem renderizou a linha, nunca dentro
 * do modal.
 */
export interface FiltrosDeRecorteDePedido {
  situacoes?: string[];
  comprador?: string;
  solicitante?: string;
  placa?: string;
  oficina?: string;
  centrosCusto?: string[];
  /** `sem` abre a linha "Sem requisição de origem". */
  origem?: 'com' | 'sem';
  /**
   * at-129 — o filtro do card "Acompanhamento de entrega": os estados da coluna "Chegada".
   * Substituiu o `entrega` (entregue × aguardando), que o servidor passou a ignorar.
   */
  chegada?: SituacaoDaChegada[];
}

/** `situacao` aqui é o filtro do card (at-110) — ver a nota em `fetchCompradoresCompras`. */
export const fetchRequisicoesPorPlaca = (
  f: FiltrosCompras & { busca?: string; pagina?: number; limite?: number; situacoes?: string[] },
) => buscar<RequisicoesPorPlaca>('placas', f);

// ---------------------------------------------------------------------------------------------
// at-111 — o painel por setor, que a at-112 reancorou no CENTRO DE CUSTO.
//
// 🔴 O card prova, na tela, que nenhuma requisição fica de fora: a soma das linhas é o total do
// recorte, e o que não tem centro de custo aparece em "Sem centro de custo" (891 requisições em
// 22/09/2026). Os outros dois cards ocupam, quando o recorte não é da oficina, o lugar dos de
// oficina e placa — que ali não respondem nada: a oficina aparece em 2 de 790 requisições.
// ---------------------------------------------------------------------------------------------

/**
 * O valor que abre a linha "Local não informado".
 *
 * ⚠️ Sentinela, e não parâmetro ausente: ausente significa "todos os setores do recorte". Precisa
 * ser **idêntico** ao do backend (`SEM_LOCAL` em `compras-dashboard.service.ts`) — duas grafias
 * fariam o clique cair num filtro que não existe, e a lista abriria vazia sem erro nenhum.
 */
export const SEM_LOCAL = 'SEM_LOCAL';

export interface RequisicoesPorCentroCusto {
  centros: Array<{
    /** A pk do centro no ERP; `null` é a linha "Sem centro de custo". */
    centroCusto: string | null;
    /** O código da tela do ERP (`OFIE25`), quando há. */
    codigo: string | null;
    descricao: string;
    grupo: GrupoCentroCusto | null;
    requisicoes: number;
    abertas: number;
    aguardandoEntrega: number;
  }>;
  /** 🔴 O total do recorte: é ele que a soma das linhas tem de fechar. */
  total: number;
  /** `true` enquanto a camada CADASTROS não tiver rodado: tudo cai em "Sem centro de custo". */
  cadastroVazio: boolean;
  janela: { de: string; ate: string };
}

export interface RequisicoesPorSolicitante {
  solicitantes: Array<{
    id: string;
    nome: string | null;
    /** Os setores em que a pessoa pediu — uma linha por pessoa, nunca por par. */
    setores: string[];
    requisicoes: number;
    abertas: number;
    aguardandoEntrega: number;
  }>;
  /** Obrigatória na tela: nem toda requisição traz solicitante identificado. */
  cobertura: { comSolicitante: number; total: number };
  janela: { de: string; ate: string };
}

export interface ItensMaisRequisitados {
  /** ⚠️ Sem valor: a requisição não tem preço — ele nasce no pedido de compra. */
  itens: Array<{ descricao: string; requisicoes: number; quantidade: number }>;
  /** Quantos itens distintos existem no recorte — o corte do top é declarado, nunca silencioso. */
  totalDeItens: number;
  cobertura: { requisicoesComItem: number; total: number };
  janela: { de: string; ate: string };
}

export const fetchRequisicoesPorCentroCusto = (f: FiltrosCompras) =>
  buscar<RequisicoesPorCentroCusto>('por-centro-custo', f);

/**
 * O cadastro que alimenta o filtro (at-112).
 *
 * ⚠️ Não recebe filtros de propósito: o card conta o período escolhido, e um centro sem movimento
 * naquele mês sumiria da lista de opções — deixando inalcançável justamente o recorte que o
 * usuário quer investigar.
 */
export const fetchCentrosDeCusto = () => buscar<CentrosDeCusto>('centros-custo');
export const fetchRequisicoesPorSolicitante = (f: FiltrosCompras) =>
  buscar<RequisicoesPorSolicitante>('por-solicitante', f);
export const fetchItensMaisRequisitados = (f: FiltrosCompras) =>
  buscar<ItensMaisRequisitados>('itens-mais-requisitados', f);

/** As peças que compõem o "Itens distintos" do card de um comprador (at-115). */
export interface ItensDistintosDoComprador {
  itens: Array<{
    /** `null` é o item sem código no ERP — contado pelo card, então listado aqui. */
    codigo: string | null;
    descricao: string | null;
    requisicoes: number;
    quantidade: number;
  }>;
  /** Do recorte inteiro: é o número que a tela confronta com o card. */
  total: number;
  pagina: number;
  porPagina: number;
  janela: { de: string; ate: string };
}

/**
 * 🔴 Leva o filtro do painel **e** o status do card, porque o número que o clique abre foi contado
 * com os dois. Sem `situacoes`, a lista somaria mais do que a barra que a abriu (at-110).
 *
 * ⚠️ Agrupa por **código**, enquanto "Itens mais requisitados" agrupa por descrição: são rotas
 * separadas porque os dois números são diferentes.
 */
export const fetchItensDistintosDoComprador = (
  f: FiltrosCompras & { situacoes?: string[] },
  pagina: number,
  porPagina = 25,
) => buscar<ItensDistintosDoComprador>('itens-distintos', { ...f, pagina, limite: porPagina });

/**
 * Os pedidos que compõem uma linha do card de fornecedores (at-110).
 *
 * ⚠️ `fornecedor` é a **pk** do ERP, e a **ausência dele significa a linha "Fornecedor não
 * identificado"** — não "todos". A rota só é chamada pelo clique, e aquela linha precisa de um
 * jeito de ser aberta; sem isso ela seria a única inclicável da tabela.
 */
export const fetchPedidosDoFornecedor = (
  f: FiltrosCompras & FiltrosDaLista & { fornecedor?: string; pagina?: number; limite?: number },
) => buscar<PedidosComOrigem & { filtrosNaoAplicados: string[] }>('fornecedores/pedidos', f);

/**
 * Um pedido de compra inteiro — o clique no número, nas listas de pedido (at-110).
 *
 * 🔴 `itensNaoSincronizados` é campo de CONTRATO: ele distingue "este pedido não tem itens" de "a
 * carga histórica de itens ainda não rodou" (um disparo manual de ~3 h, at-109). Medido em
 * 21/09/2026: o espelho de dev tem 9.566 pedidos e **zero** itens. Sem ele, a tela afirmaria algo
 * sobre a compra a partir de uma sincronização que não aconteceu.
 */
export interface DetalheDoPedido {
  id: string;
  numero: string | null;
  emissao: string | null;
  fornecedorNome: string | null;
  situacao: string | null;
  total: number;
  /** Apelido livre digitado pelo comprador — NÃO é a oficina nem o elo com a requisição. */
  numeroExterno: string | null;
  previsaoDeEntrega: string | null;
  situacaoPrevisao: SituacaoDaPrevisao;
  operacao: string | null;
  /** A origem herdada da requisição (at-109). Nula em compra direta, ~4 em 100. */
  requisicaoCodigo: string | null;
  compradorNome: string | null;
  placa: string | null;
  oficina: string | null;
  itens: Array<{
    id: string;
    /** UUID do produto no ERP. A tela mostra código e descrição, abaixo. */
    produto: string | null;
    /** at-122 — do cadastro de produtos do Nasajon, resolvido pela sincronização. */
    produtoCodigo: string | null;
    produtoDescricao: string | null;
    /**
     * 🔴 `PENDENTE` é ausência de SINCRONIZAÇÃO (o produto ainda não foi buscado), não fato sobre a
     * compra; `NAO_ENCONTRADO` é o cadastro do Nasajon não ter devolvido o id.
     */
    produtoSituacao: 'IDENTIFICADO' | 'PENDENTE' | 'NAO_ENCONTRADO';
    quantidade: number | null;
    /** Aqui o valor EXISTE, ao contrário do item de requisição. */
    valorUnitario: number | null;
    total: number | null;
  }>;
  itensNaoSincronizados: boolean;
}

export const fetchDetalheDoPedido = (id: string) =>
  buscar<DetalheDoPedido>(`pedido/${encodeURIComponent(id)}`);

/**
 * Uma requisição inteira, com as peças pedidas (at-110).
 *
 * 🔴 **Não existe valor de item, e a ausência é medida**: `valor_unitario` é zero em 100% dos
 * 99.333 itens do espelho (21/09/2026). A requisição é o pedido interno de peça; o preço nasce no
 * **pedido de compra**, depois da cotação. Uma coluna de zeros seria lida como "esta peça não custa
 * nada".
 *
 * ⚠️ `unidade` e `objeto` do ERP também ficam de fora: vêm como UUID, sem de-para no espelho.
 */
export interface DetalheDaRequisicao {
  id: string;
  codigo: string | null;
  dataCadastro: string | null;
  situacao: string | null;
  estadoWorkflow: string | null;
  compradorNome: string | null;
  solicitanteNome: string | null;
  oficina: string | null;
  placa: string | null;
  local: string | null;
  /** at-112: o local de uso (de onde o centro de custo vem) e o centro, com o código do ERP. */
  localUso: string | null;
  centroCusto: { codigo: string | null; descricao: string | null } | null;
  /** A justificativa INTEIRA — a lista mostra só a primeira linha. */
  justificativa: string | null;
  itens: Array<{
    id: string;
    ordem: number | null;
    codigo: string | null;
    /** Já normalizada no servidor: o ERP manda quebra de linha no meio do texto. */
    descricao: string | null;
    quantidade: number | null;
    /** Só vem quando NÃO é `produto` (ex.: `comentario`, que não é peça). */
    tipo: string | null;
  }>;
}

export const fetchDetalheDaRequisicao = (id: string) =>
  buscar<DetalheDaRequisicao>(`requisicoes/${encodeURIComponent(id)}`);

/** Um pedido medido (at-125): a previsão do pedido contra a entrada da sua última nota. */
export interface EntregaMedida {
  pedido: string;
  fornecedor: string;
  /** As notas que atenderam o pedido; ele conta como chegado na data da última. */
  notas: Array<{ nota: string; dataEntrada: string }>;
  emissao: string | null;
  /** A previsão registrada no pedido. */
  prometido: string;
  /** A data de entrada da última nota. */
  chegada: string;
  /** Negativo = chegou adiantada; 0 = no dia; positivo = dias de atraso. */
  diasDeAtraso: number;
  /** at-90: comentários do pedido — o selo na linha. */
  comentarios?: number;
}

export interface EntregasDoFornecedor {
  fornecedor: string | null;
  total: number;
  noPrazo: number;
  pagina: number;
  porPagina: number;
  entregas: EntregaMedida[];
  janela: { de: string; ate: string };
}

/**
 * As entregas que compõem a pontualidade de um fornecedor (at-110).
 *
 * ⚠️ `fornecedor` é o **nome** como o relatório o traz — assimetria consciente com a função
 * acima, que usa pk. É o mesmo texto que o card exibe na tabela.
 *
 * ⚠️ Os pedidos descartados (sem previsão, previsão ou chegada anteriores à emissão) **não**
 * aparecem aqui: não foram medidos, e estão declarados na cobertura do card.
 */
export const fetchEntregasDoFornecedor = (
  f: FiltrosCompras & FiltrosDaLista & { fornecedor?: string; pagina?: number; limite?: number },
) => buscar<EntregasDoFornecedor>('entrega-no-prazo/detalhe', f);

export const fetchPedidosComOrigem = (
  f: FiltrosCompras & FiltrosDeRecorteDePedido & FiltrosDaLista & { pagina?: number; limite?: number; situacao?: string },
) => buscar<PedidosComOrigem>('pedidos/lista', f);

/** at-121: `busca`, `pagina`, `limite` e `situacoes` são do CARD; a busca é feita no servidor. */
export const fetchPedidosAgrupados = (
  f: FiltrosCompras & {
    por: AgrupamentoDePedido;
    busca?: string;
    pagina?: number;
    limite?: number;
    situacoes?: string[];
  },
) => buscar<PedidosAgrupados>('pedidos/agrupados', f);

/** at-121: os KPIs e o funil da aba Pedidos. Sem catch — quem trata o erro é a tela. */
export const fetchPedidosResumo = (f: FiltrosCompras) => buscar<ResumoPedidos>('pedidos/resumo', f);

// ---------------------------------------------------------------------------------------------
// Configuração (ADMIN e TI) — os de-paras que o ERP não entrega.
// ---------------------------------------------------------------------------------------------

export interface OficinaAliasConfig {
  grafia: string;
  oficinaCanonica: string;
  automatico: boolean;
  ocorrencias: number;
  /**
   * transversal/at-99: a oficina do cadastro de Oficina Externa a que a grafia está ligada. Ligada,
   * o painel usa o nome do cadastro. Opcionais porque o backend anterior à at-99 não os manda.
   */
  oficinaExternaId?: string | null;
  oficinaExternaNome?: string | null;
  /** `false` = ligada a uma oficina que foi desativada no cadastro. */
  oficinaExternaAtiva?: boolean | null;
}

/** Uma oficina ativa do cadastro de Oficina Externa — as opções do select (at-99). */
export interface OficinaExternaOpcao {
  id: string;
  nome: string;
  regiao: string | null;
}

export interface LocalConfig {
  pk: string;
  codigo: string | null;
  descricao: string | null;
  grupo: 'PECAS' | 'OUTROS';
  requisicoes: number;
  /**
   * at-111 — quantas requisições deste local trazem OFICINA no texto: o padrão de compra de peça.
   *
   * ⚠️ Oficina, nunca placa: medido em 21/09/2026, a placa aparece em 101 de 222 requisições de
   * Insumos e em 27 de 178 de Marketing (é o terceiro degrau do parser, que lê padrão solto e
   * erra). Com placa no critério, o aviso apareceria todo dia sem nada errado.
   */
  comOficina: number;
  /** Alguém já escolheu o setor deste local nesta tela — o que desliga o aviso. */
  classificadoPorPessoa: boolean;
  /** 🔴 Local em "Outro setor" com cara de peça e sem ninguém ter decidido isso. */
  suspeito: boolean;
}

export async function fetchConfigOficinas(): Promise<{
  oficinas: OficinaAliasConfig[];
  oficinasExternas?: OficinaExternaOpcao[];
}> {
  const { data } = await axios.get(`${BACKEND_URL}/compras/config/oficinas`);
  return data;
}

export async function salvarConfigOficinas(
  oficinas: Array<{ grafia: string; oficinaCanonica: string; oficinaExternaId?: string | null }>,
) {
  const { data } = await axios.put(`${BACKEND_URL}/compras/config/oficinas`, { oficinas });
  return data;
}

export async function fetchConfigLocais(): Promise<{ locais: LocalConfig[] }> {
  const { data } = await axios.get(`${BACKEND_URL}/compras/config/locais`);
  return data;
}

export async function definirGrupoDoLocal(pk: string, grupo: 'PECAS' | 'OUTROS') {
  const { data } = await axios.patch(`${BACKEND_URL}/compras/config/locais/${pk}`, { grupo });
  return data;
}

/**
 * Um centro de custo do ERP e o grupo dele — transversal/at-94. O grupo decide quais cards o
 * painel monta (os de oficina ou os de outros setores).
 */
export interface CentroDeCustoConfig {
  pk: string;
  codigo: string | null;
  descricao: string | null;
  grupo: 'PECAS' | 'OUTROS';
  /** Requisições do período inteiro cujo local de uso aponta para este centro. */
  requisicoes: number;
  /** Alguém já escolheu o grupo deste centro nesta tela. */
  classificadoPorPessoa: boolean;
}

export async function fetchConfigCentrosDeCusto(): Promise<{ centros: CentroDeCustoConfig[] }> {
  const { data } = await axios.get(`${BACKEND_URL}/compras/config/centros-custo`);
  return data;
}

export async function definirGrupoDoCentroDeCusto(pk: string, grupo: 'PECAS' | 'OUTROS') {
  const { data } = await axios.patch(`${BACKEND_URL}/compras/config/centros-custo/${pk}`, { grupo });
  return data;
}

/**
 * Uma classificação financeira do ERP, e se ela exige oficina de destino — at-113.
 *
 * É este cadastro que define o **universo** do card de oficinas: quais compras entram nele e, por
 * consequência, o que significa a linha "Oficina não informada".
 */
export interface ClassificacaoFinanceiraConfig {
  pk: string;
  /** O código do ERP (`02.25.001`) — é ele, e não a descrição, que a semeadura usa. */
  codigo: string | null;
  descricao: string | null;
  exigeOficina: boolean;
  requisicoes: number;
  /** Quantas delas trazem oficina no texto — é o percentual que denuncia marcação errada. */
  comOficina: number;
  /** Alguém já decidiu esta linha nesta tela, o que desliga o aviso. */
  classificadaPorPessoa: boolean;
  /** 🔴 Não marcada, mas a maioria das requisições dela traz oficina preenchida. */
  suspeita: boolean;
}

export async function fetchConfigClassificacoes(): Promise<{
  classificacoes: ClassificacaoFinanceiraConfig[];
}> {
  const { data } = await axios.get(`${BACKEND_URL}/compras/config/classificacoes`);
  return data;
}

export async function definirExigeOficina(pk: string, exigeOficina: boolean) {
  const { data } = await axios.patch(`${BACKEND_URL}/compras/config/classificacoes/${pk}`, {
    exigeOficina,
  });
  return data;
}

/**
 * @param itens só vale em `PEDIDOS` (at-109): traz também os itens de cada pedido. É a carga
 * histórica, bem mais lenta — a varredura de 12 meses passa de ~46 min para ~3 h.
 */
export async function dispararSyncCompras(camada: CamadaSync, itens = false) {
  const { data } = await axios.post(`${BACKEND_URL}/compras/sync/${camada}`, itens ? { itens } : {});
  return data as { disparado: boolean; jobId?: string; motivo?: string };
}

// ---------------------------------------------------------------------------------------------
// Pausa e ritmo da sincronização (at-108) — ADMIN e TI.
// ---------------------------------------------------------------------------------------------

export async function fetchEstadoDaSincronizacao(): Promise<EstadoDaSincronizacao> {
  const { data } = await axios.get(`${BACKEND_URL}/compras/config/sync`);
  return data;
}

export async function definirPausaDoSync(pausado: boolean): Promise<EstadoDaSincronizacao> {
  const { data } = await axios.put(`${BACKEND_URL}/compras/config/sync/pausa`, { pausado });
  return data;
}

export async function definirModoDoSync(modo: ModoSync): Promise<EstadoDaSincronizacao> {
  const { data } = await axios.put(`${BACKEND_URL}/compras/config/sync/modo`, { modo });
  return data;
}
